'use client';

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Image from 'next/image';
import { useAuth } from '@/contexts/AuthContext';
import { tmdb } from '@/lib/tmdb';
import { PlaylistService, Playlist } from '@/lib/playlists';
import { toast } from 'sonner';
import { ListVideo, Plus, Check, Loader2 } from 'lucide-react';

// Compact "add to playlist" button with a popover: shows the user's
// playlists with in/out state, toggles membership, and can create a new
// playlist inline. Used on watchlist/media cards.

interface AddToPlaylistButtonProps {
  media: {
    id: number;
    type: 'movie' | 'tv';
    title: string;
    posterPath: string | null;
    year?: string | null;
  };
  className?: string;
  triggerClassName?: string;
  iconClassName?: string;
}

export function AddToPlaylistButton({ media, className = '', triggerClassName = '', iconClassName = 'h-4 w-4' }: AddToPlaylistButtonProps) {
  const { user, userProfile } = useAuth();
  const [open, setOpen] = useState(false);
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [loading, setLoading] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [creating, setCreating] = useState(false);
  // Portal position — the popover renders at document level so poster
  // containers with overflow-hidden can't clip it
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number; openUp: boolean } | null>(null);
  const areaRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Close on outside click / Escape / scroll
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (
        areaRef.current && !areaRef.current.contains(target) &&
        popoverRef.current && !popoverRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const loadPlaylists = async () => {
    if (!user) return;
    setLoading(true);
    try {
      setPlaylists(await PlaylistService.getUserPlaylists(user.uid));
    } catch (error) {
      toast.error('Failed to load playlists');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const next = !open;
    if (next && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const popoverWidth = 256; // w-64
      const openUp = window.innerHeight - rect.bottom < 380 && rect.top > 380;
      setPopoverPos({
        top: openUp ? rect.top - 8 : rect.bottom + 8,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - popoverWidth - 8)),
        openUp,
      });
    }
    setOpen(next);
    if (next) loadPlaylists();
  };

  const isInPlaylist = (playlist: Playlist) =>
    playlist.items.some(i => i.id === media.id && i.type === media.type);

  const handleToggle = async (playlist: Playlist) => {
    const inList = isInPlaylist(playlist);
    const items = inList
      ? playlist.items.filter(i => !(i.id === media.id && i.type === media.type))
      : [
          ...playlist.items,
          {
            id: media.id,
            type: media.type,
            title: media.title,
            posterPath: media.posterPath,
            year: media.year || null,
            addedAt: new Date().toISOString(),
          },
        ];

    // Optimistic
    setPlaylists(prev => prev.map(p => (p.id === playlist.id ? { ...p, items } : p)));
    try {
      await PlaylistService.updatePlaylist(playlist.id, { items });
      toast.success(inList ? `Removed from ${playlist.title}` : `Added to ${playlist.title}`);
    } catch (error) {
      setPlaylists(prev => prev.map(p => (p.id === playlist.id ? playlist : p)));
      toast.error('Failed to update playlist');
    }
  };

  const handleCreate = async () => {
    if (!user || !newTitle.trim()) return;
    setCreating(true);
    try {
      const ownerName = userProfile?.username || user.email?.split('@')[0] || 'Someone';
      const id = await PlaylistService.createPlaylist(user.uid, ownerName, newTitle.trim());
      const created = await PlaylistService.getPlaylist(id);
      if (created) {
        await handleToggle(created); // adds the item
        setPlaylists(prev => [
          { ...created, items: [{ id: media.id, type: media.type, title: media.title, posterPath: media.posterPath, year: media.year || null, addedAt: new Date().toISOString() }] },
          ...prev.filter(p => p.id !== id),
        ]);
      }
      setNewTitle('');
    } catch (error) {
      toast.error('Failed to create playlist');
    } finally {
      setCreating(false);
    }
  };

  if (!user) return null;

  return (
    <div ref={areaRef} className={`relative ${className}`}>
      <button
        ref={triggerRef}
        onClick={handleOpen}
        className={triggerClassName || 'flex items-center justify-center w-8 h-8 md:w-9 md:h-9 rounded-full bg-black/60 backdrop-blur-md ring-1 ring-white/15 text-gray-300 hover:text-white hover:ring-red-500/50 hover:scale-110 active:scale-95 transition-all shadow-xl'}
        title="Add to playlist"
      >
        <ListVideo className={iconClassName} />
      </button>

      {open && popoverPos && typeof document !== 'undefined' && createPortal(
        <div
          ref={popoverRef}
          // preventDefault too: the card may be wrapped in a <Link>, and
          // clicks inside the popover must never navigate
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          style={{
            position: 'fixed',
            top: popoverPos.top,
            left: popoverPos.left,
            transform: popoverPos.openUp ? 'translateY(-100%)' : undefined,
          }}
          className="z-[100] w-64 rounded-xl bg-gray-900 ring-1 ring-white/15 shadow-2xl overflow-hidden"
        >
          <p className="px-3 pt-2.5 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500 border-b border-white/10">
            Add to playlist
          </p>

          <div className="max-h-56 overflow-y-auto">
            {loading ? (
              <div className="p-4 flex items-center justify-center text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            ) : playlists.length === 0 ? (
              <p className="p-3 text-xs text-gray-500">No playlists yet — create one below.</p>
            ) : (
              playlists.map((playlist) => {
                const inList = isInPlaylist(playlist);
                const cover = playlist.items.find(i => i.posterPath)?.posterPath;
                return (
                  <button
                    key={playlist.id}
                    onClick={() => handleToggle(playlist)}
                    className="w-full flex items-center gap-2.5 p-2 hover:bg-white/5 transition-colors text-left"
                  >
                    <div className="w-7 h-10 rounded overflow-hidden relative flex-shrink-0 bg-gray-800">
                      {cover && (
                        <Image src={tmdb.getImageUrl(cover, 'w200')} alt="" fill sizes="28px" className="object-cover" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{playlist.title}</p>
                      <p className="text-[10px] text-gray-500">{playlist.items.length} title{playlist.items.length !== 1 ? 's' : ''}</p>
                    </div>
                    <div
                      className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 transition-colors ${
                        inList ? 'bg-red-600' : 'ring-1 ring-white/25'
                      }`}
                    >
                      {inList && <Check className="h-3.5 w-3.5 text-white" />}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Create new playlist inline */}
          <div className="p-2 border-t border-white/10 flex gap-1.5">
            <input
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              placeholder="New playlist..."
              className="flex-1 min-w-0 h-8 rounded-lg bg-white/5 ring-1 ring-white/10 border-0 px-2.5 text-xs text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500/60"
            />
            <button
              onClick={handleCreate}
              disabled={creating || !newTitle.trim()}
              className="flex items-center justify-center w-8 h-8 rounded-lg bg-red-600 hover:bg-red-500 text-white disabled:opacity-40 transition-colors flex-shrink-0"
              title="Create and add"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
